import React, { useCallback, useEffect, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, { Column, Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import BadgeStorageStatus from "../../../../../../../components/Generic/BadgeStorageStatus";
import ConfirmStatusModal from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { PropHandles } from "../../../../../../../components/Generic/ViewJobComponent";
import { LIFTVAN, STORAGE_STATUS } from "../../../../../../../constants";
import { useAppState } from "../../../../../../../context/StateContext";
import { useStorageBoxHook } from "../../../../../../../hooks/useStorageBoxActions";
import { Branch } from "../../../../../../../models/branch";
import { Row, Searches } from "../../../../../../../models/shipeezi.table";
import { StorageBox, StorageBoxDto, StorageStatus } from "../../../../../../../models/storage.box";
import { StorageBoxType } from "../../../../../../../models/storage.box.type";
import { Warehouse } from "../../../../../../../models/warehouse";
import BranchService from "../../../../../../../services/branch.service";
import StorageBoxTypeService from "../../../../../../../services/storage.box.type.service";
import { Status, StorageStatus as EStorageStatus } from "../../../../../../../util/enums";
import { saveRowsSelectedFromTable } from "../../../../../../../util/selectRowsFromTable";
import ConfirmDeleteStorageBoxModal, {
  DeleteStorageBoxHandle,
} from "./ConfirmDeleteStorageBoxModal";
import { StorageBoxesPageModal } from "./StorageBoxesPageModal";
import { StorageBoxViewModal } from "./StorageBoxesViewModal";
import {
  BadgeStatus,
  ButtonChangeStatus,
  ChangeStatusIcon,
  DeleteIcon,
  ImportIcon,
  ViewIcon,
} from "./styles";
import UploadStorageBoxModal, { UploadCSVFileHandles } from "./UploadStorageBoxModal";
import { isShowAction } from "Util/permissions";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
  };
};

export const StorageBoxesPage = ({ permission }: Props) => {
  const {
    page,
    total,
    hasError,
    pageSize,
    setPageSize,
    handleSearch,
    handleFilter,
    handleSorting,
    storageBoxesData,
    handleCurrentPage,
    handleGetDefaultFilters,
    handleGetDefaultBranchFilters,
    useCreateStorageBox,
    useUpdateStorageBox,
    getStorageBoxesList,
    getStorageBoxesCount,
  } = useStorageBoxHook();

  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const uploadCSVRef = useRef<UploadCSVFileHandles>(null);

  const [openNewStorageBoxModal, setOpenNewStorageBoxModal] = useState(false);
  const [selectedStorageBox, setSelectedStorageBox] = useState<StorageBox>();
  const [loadedStorageBoxes, setLoadedStorageBoxes] = useState<StorageBox[]>([]);
  const [warehousesList, setWarehousesList] = useState<Warehouse[]>([]);
  const [storageBoxTypesList, setStorageBoxTypesList] = useState<StorageBoxType[]>([]);
  const [branchesList, setBranchesList] = useState<Branch[]>([]);

  const [disableSortingType, setDisableSortingType] = useState<boolean>(false);
  const [disableSortingStatus, setDisableSortingStatus] = useState<boolean>(false);
  const [disableSortingWarehouse, setDisableSortingWarehouse] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [cleanSelection, setCleanSelection] = useState<boolean>(false);

  const sideViewRef = useRef<PropHandles>(null);
  const deleteStorageBoxModalRef = useRef<DeleteStorageBoxHandle>(null);

  const userBranches = currentUser?.branches?.filter(
    (branch) => branch.companyId === currentUser?.companyId
  );

  useEffect(() => {
    if (!hasError) {
      setCleanSelection(false);
      setRows([]);
    }
  }, [hasError]);

  const onOpenConfirmationModal = (storageBox?: StorageBox) => {
    if (storageBox) deleteStorageBoxModalRef?.current?.onOpenModal(storageBox);
    else deleteStorageBoxModalRef?.current?.onCloseModal();
  };

  const onDelete = (storageBoxToDelete?: StorageBoxDto) => {
    if (storageBoxToDelete) {
      updateStorageBox({
        id: Number(storageBoxToDelete.id),
        storageBox: {
          ...storageBoxToDelete,
          valid: false,
        },
      });
    }
  };

  const enableOrDisableSorting = (searches: Searches) => {
    setDisableSortingType(false);
    setDisableSortingStatus(false);
    setDisableSortingWarehouse(false);

    const propertyNames = searches?.map((search) => Object.entries(search)?.[0]);

    if (propertyNames?.[0]?.includes("storageStatus")) {
      setDisableSortingStatus(true);
    }
    if (propertyNames?.[0]?.includes("storageBoxType")) {
      setDisableSortingType(true);
    }
    if (propertyNames?.[0]?.includes("warehouses")) {
      setDisableSortingWarehouse(true);
    }
  };

  const { isFetching } = getStorageBoxesList();

  getStorageBoxesCount();

  const { mutate: updateStorageBox, isLoading: isUpdating } = useUpdateStorageBox();
  const { mutate: createStorageBox, isLoading: isCreating } = useCreateStorageBox();

  const { isFetching: isFetchingStorageBoxTypes } = useQuery(
    ["storage.box.types.list", currentUser?.companyId],
    () =>
      StorageBoxTypeService.getAll({
        isActive: "1",
        systemDefault: "0",
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      onSuccess: (response) => {
        if (response && response.data) {
          setStorageBoxTypesList(
            response.data.filter((storageBoxType) => storageBoxType.name !== LIFTVAN)
          );
        }
      },
    }
  );

  const getBranchesId = () => {
    const branches = currentUser?.branches
      ?.filter((branch) => branch.companyId === currentUser.companyId)
      ?.map((branch) => branch?.id);

    if (!branches?.length) return [];

    return branches;
  };

  const { isFetching: isFetchingBranchesList } = useQuery(
    ["branch.list"],
    () =>
      BranchService.getAll({
        id: getBranchesId(),
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          {
            required: true,
            association: "warehouses",
            attributes: ["id", "code", "name", "companyId"],
            where: { companyId: currentUser?.companyId, status: "Active" },
            include: [
              {
                association: "sections",
                attributes: ["id", "name", "allowsStorageBox"],
                required: true,
                where: { allowsStorageBox: true },
                include: [
                  {
                    association: "storageBoxTypes",
                    attributes: ["id", "name"],
                  },
                ],
              },
            ],
            order: ["name", "asc"],
          },
        ]),
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          const branches = response.data;
          const warehouses: Warehouse[] = [];

          branches?.forEach((branch) =>
            branch?.warehouses?.forEach((warehouseInBranch) => {
              if (
                !warehouses.find((warehouseInList) => warehouseInList?.id === warehouseInBranch?.id)
              )
                warehouses.push(warehouseInBranch);
            })
          );

          warehouses?.sort((a, b) => {
            if (a?.name && b?.name) {
              if (a?.name?.toLowerCase() > b?.name?.toLowerCase()) {
                return 1;
              }
              if (a?.name?.toLowerCase() < b?.name?.toLowerCase()) {
                return -1;
              }
            }

            return 0;
          });

          setBranchesList(branches);
          setWarehousesList(warehouses);
        }
      },
    }
  );

  const viewStorageBox = (index: number) => {
    const storageBoxToView = storageBoxesData.find(
      (storageBox) => Number(storageBox.id) === Number(index)
    );

    return (
      <Tooltip title={translate("view")} placement="top">
        <div>
          <ViewIcon
            size={18}
            color="#475467"
            onClick={() => {
              setSelectedStorageBox(storageBoxToView);
              sideViewRef.current?.setOpenSideBar(true);
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const printStorageBox = () => {
    return null;

    // TODO: Open a model to generate pdf with the storage box number
    // return (
    //   <Tooltip title="This action is still in development." placement="top">
    //     <div>
    //       <GridIcons.print size={18} color="#D0D5DD" onClick={() => {}} />
    //     </div>
    //   </Tooltip>
    // );
  };

  const changeStatusStorageBox = (_: number, storageBox: StorageBox) => {
    if (storageBox?.storageStatus && storageBox?.storageStatus?.name === EStorageStatus.EMPTY) {
      return (
        <Tooltip title={translate("changeStatus")} placement="top">
          <div>
            <ChangeStatusIcon
              data-cy="action-change"
              color="#475467"
              onClick={() => {
                if (storageBox?.id) {
                  updateStorageBox({
                    id: Number(storageBox?.id),
                    storageBox: {
                      isActive: !storageBox.isActive,
                    } as StorageBoxDto,
                  });
                }
              }}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const deleteStorageBox = (_: number, storageBox: StorageBox) => {
    if (storageBox?.storageStatus && storageBox?.storageStatus?.name === EStorageStatus.EMPTY) {
      return (
        <Tooltip title={translate("delete")} placement="top">
          <div>
            <DeleteIcon
              size={18}
              color="#F04438"
              onClick={() => {
                if (storageBox) {
                  onOpenConfirmationModal(storageBox);
                }
              }}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderWarehousesNames = (warehouses: { name: string }[]) => {
    if (warehouses && warehouses.length > 0) {
      const warehousesNames = warehouses.map((jobType) => jobType?.name);

      return warehousesNames.join(" / ");
    }
  };

  const columns: Column[] = [
    { name: "number", title: translate("number") },
    {
      name: "storageBoxType",
      searchIdentifier: "storageBoxType",
      title: translate("type"),
      formatComponent: (storageBoxType: StorageBoxType) =>
        storageBoxType && storageBoxType?.name && storageBoxType?.name,
      hideSort: disableSortingType,
    },
    {
      name: "warehouses",
      title: translate("warehouse"),
      searchIdentifier: "warehouses",
      formatComponent: renderWarehousesNames,
      hideSort: disableSortingWarehouse,
    },
    {
      name: "updatedAt",
      title: translate("lastUpdate"),
      hideSearch: true,
      formatComponent: (updatedAt?: Date) =>
        updatedAt &&
        currentUser?.preferences && (
          <span>{formatDateWithUserPref(updatedAt, currentUser.preferences, "dateOnly")}</span>
        ),
    },
    {
      name: "storageStatus",
      searchIdentifier: "storageStatus",
      title: translate("storageStatus"),
      formatComponent: (storageStatus: StorageStatus) => (
        <BadgeStorageStatus value={storageStatus?.name} />
      ),
      hideSort: disableSortingStatus,
    },
    {
      name: "isActive",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (status: boolean) => (
        <BadgeStatus isActive={status}>{status ? Status.ACTIVE : Status.INACTIVE}</BadgeStatus>
      ),
    },
  ];

  const renderButtonToImportStorageBox = () => {
    if (!currentUser?.isAdmin) return null;

    return (
      <Button
        customColor={Color.SECONDARY_OUTLINED}
        onClick={() => uploadCSVRef.current?.open()}
        data-cy="importStorageBox"
      >
        <ImportIcon />
        {translate("import")}
      </Button>
    );
  };

  const editStorageBox = (storageBoxId: number) => {
    const storageBoxToUpdate = storageBoxesData.find(
      (storageBox) => Number(storageBox.id) === Number(storageBoxId)
    );

    if (storageBoxToUpdate) {
      setSelectedStorageBox(storageBoxToUpdate);
      setOpenNewStorageBoxModal(true);
    }
  };

  const searchTable = (searches: Searches) => {
    handleSearch(searches);
    enableOrDisableSorting(searches);
  };

  const handleSaveStorageBoxSelected = (rowsArr: number[]) => {
    const storageBoxes = storageBoxesData?.map((storageBox) => ({
      ...storageBox,
      status: storageBox?.isActive ? Status.ACTIVE : Status.INACTIVE,
    }));
    const rowsSelected: Row[] = saveRowsSelectedFromTable(rowsArr, rows, storageBoxes);

    const selectedStorageBoxes = [...rowsSelected] as StorageBox[];
    const selectedLoadedStorageBoxes: StorageBox[] = selectedStorageBoxes.filter(
      (storageBox) =>
        storageBox?.storageStatus?.name !== undefined &&
        storageBox?.storageStatus?.name !== EStorageStatus.EMPTY
    );

    setLoadedStorageBoxes(selectedLoadedStorageBoxes);

    if (rowsArr.length === 0) setRows([]);
    else setRows([...rowsSelected]);
  };

  const renderButtonToChangeStatus = useCallback(() => {
    if (rows.length > 0 && rows.length !== loadedStorageBoxes.length) {
      return (
        <ButtonChangeStatus
          tooltip={translate("changeStatus")}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} />
          {translate("changeStatus")}
        </ButtonChangeStatus>
      );
    }
  }, [rows]);

  const handleWarnLoadedStorageBoxes = (loadedStorageBoxes: StorageBox[]) => {
    const storageBoxesNumbers = loadedStorageBoxes.map((loaded) => loaded.number).join(", ");

    return NotificationToast.warning(
      `${translate(
        "theFollowingStorageBoxesAreLoadedAndCanNotChangeStatus"
      )} ${storageBoxesNumbers}`,
      { id: "notification.warning.loaded.storage.boxes" }
    );
  };

  const getUnloadedStorageBoxes = (selectedStorageBoxes: StorageBox[]) => {
    return selectedStorageBoxes.filter(
      (storageBox) =>
        storageBox?.storageStatus?.name !== undefined &&
        storageBox?.storageStatus?.name === EStorageStatus.EMPTY
    );
  };

  return (
    <div data-cy="storageBoxesGrid">
      <ShipeeziNewGrid
        data={storageBoxesData}
        columns={columns}
        totalItems={total}
        loadingData={isFetching || isUpdating || isCreating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[15, 40, 60, 100]}
        onChangePageSize={setPageSize}
        currentPage={page}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        onSorting={handleSorting}
        handleSelect={(rows: number[]) => handleSaveStorageBoxSelected(rows)}
        clearSelection={cleanSelection}
        generalActions={{
          orderBy: {
            right: ["handleChangeStatus", "handleImportStorageBox", "handleNewRecord"],
            left: ["handleSearch", "handleFilterBy"],
          },
          handleSearch: {
            onSearch: (filters) => searchTable(filters),
          },
          handleFilterBy: {
            categories: [
              {
                name: translate("status"),
                column: "storageBox.isActive",
                optionsSelected: handleGetDefaultFilters(),
                options: [
                  { value: "1", label: translate("active") },
                  { value: "0", label: translate("inactive") },
                ],
              },
              {
                name: translate("type"),
                column: "storageBoxType.name",
                options: storageBoxTypesList?.map((type) => type?.name),
              },
              {
                name: translate("warehouse"),
                column: "warehouses.name",
                options: warehousesList?.map((warehouse) => warehouse?.name ?? ""),
              },
              {
                name: translate("storageStatus"),
                column: "storageStatus.name",
                options: STORAGE_STATUS.map((storageStatus) => storageStatus?.name),
              },
              {
                name: translate("branches"),
                column: "branches.id",
                optionsSelected: handleGetDefaultBranchFilters(),
                options: userBranches?.map((branch) => {
                  return {
                    label: branch.name ?? "",
                    value: branch?.id ? String(branch?.id) : "",
                  };
                }),
              },
            ],
            onChange: handleFilter,
          },
          handleNewRecord:
            permission && isShowAction(currentUser, permission?.create)
              ? {
                  text: translate("new"),
                  action: () => {
                    setSelectedStorageBox(undefined);
                    setOpenNewStorageBoxModal(true);
                  },
                }
              : undefined,
          handleImportStorageBox: renderButtonToImportStorageBox(),
          handleChangeStatus: renderButtonToChangeStatus(),
        }}
        rowActions={{
          orderBy: [
            "handleEdit",
            "handleView",
            "handleChangeStatus",
            "handlePrintStorageBox",
            "handleDeleteStorageBox",
          ],
          handleEdit:
            permission && isShowAction(currentUser, permission?.edit)
              ? (storageBoxId) => editStorageBox(Number(storageBoxId))
              : undefined,
        }}
        rowActionsExtra={[
          { title: translate("view"), key: "handleView", buttonAndAction: viewStorageBox },
          {
            title: translate("changeStatus"),
            key: "handleChangeStatus",
            buttonAndAction: changeStatusStorageBox,
          },
          {
            title: translate("print"),
            key: "handlePrintStorageBox",
            buttonAndAction: printStorageBox,
          },
          {
            title: translate("delete"),
            key: "handleDeleteStorageBox",
            buttonAndAction: deleteStorageBox,
          },
        ]}
        positionActionsColumn="right"
      />
      {openNewStorageBoxModal && (
        <StorageBoxesPageModal
          open={openNewStorageBoxModal}
          hasError={hasError}
          closeModal={() => setOpenNewStorageBoxModal(false)}
          isFetchingModalData={isFetchingBranchesList || isFetchingStorageBoxTypes}
          warehouses={warehousesList}
          branches={branchesList}
          storageBoxTypes={storageBoxTypesList}
          storageBoxToUpdate={selectedStorageBox}
          createStorageBox={(storageBox: StorageBoxDto) => createStorageBox(storageBox)}
          updateStorageBox={(storageBox: StorageBoxDto, storageBoxId: number) =>
            updateStorageBox({ storageBox, id: storageBoxId })
          }
          isLoading={isUpdating || isCreating}
        />
      )}
      <UploadStorageBoxModal
        ref={uploadCSVRef}
        warehouses={warehousesList}
        branches={branchesList}
        storageBoxTypes={storageBoxTypesList}
      />
      <StorageBoxViewModal
        storageBox={selectedStorageBox}
        loading={isFetching}
        sideViewRef={sideViewRef}
      />
      <ConfirmDeleteStorageBoxModal
        ref={deleteStorageBoxModalRef}
        onDelete={onDelete}
        isDeleting={isUpdating}
        hasError={hasError}
      />
      {openConfirmation && (
        <ConfirmStatusModal
          open={openConfirmation}
          rows={getUnloadedStorageBoxes(rows as StorageBox[])}
          isLoading={isUpdating}
          hasErrors={hasError}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              const storageBox = storageBoxesData?.find((stb) => stb.id === rowId);

              if (storageBox) {
                updateStorageBox({
                  id: rowId,
                  storageBox: { isActive: status === Status.ACTIVE } as StorageBoxDto,
                });
              }

              if (loadedStorageBoxes.length > 0) {
                handleWarnLoadedStorageBoxes(loadedStorageBoxes);
              }

              setCleanSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};
